import React, { useState, useEffect } from "react";

const PurchaseForm = ({
  mode = "add",
  initialValues = {},
  onSubmit,
  onCancel,
  suppliers = [],
  products = [],
  isSubmitting = false,
}) => {
  const API_URL = process.env.REACT_APP_API_URL || "";

  // Form state
  const [supplierId, setSupplierId] = useState(initialValues.supplier_id ? String(initialValues.supplier_id) : "");
  const [productId, setProductId] = useState(initialValues.product_id ? String(initialValues.product_id) : "");
  const [unitId, setUnitId] = useState(initialValues.unit_id ? String(initialValues.unit_id) : "");
  const [unitName, setUnitName] = useState(initialValues.unit_name || "");
  const [invoiceNo, setInvoiceNo] = useState(initialValues.invoice_no || "");
  const [purchaseDate, setPurchaseDate] = useState(initialValues.purchase_date ? initialValues.purchase_date.split("T")[0] : new Date().toISOString().split("T")[0]);
  const [quantity, setQuantity] = useState(initialValues.quantity || "");
  const [perUnitPrice, setPerUnitPrice] = useState(initialValues.per_unit_price || "");
  const [paidAmount, setPaidAmount] = useState(
    initialValues.paid_amount !== undefined && initialValues.paid_amount !== null
      ? initialValues.paid_amount
      : ""
  );
  const [dueAmount, setDueAmount] = useState(initialValues.due_amount || 0);
  const [totalAmount, setTotalAmount] = useState(initialValues.total_amt || 0);
  const [paymentType, setPaymentType] = useState(initialValues.payment_type || "");

  // On mount, set unit if initialValues has it (for edit mode)
  useEffect(() => {
    if (initialValues.unit_id && initialValues.unit_name) {
      setUnitId(String(initialValues.unit_id));
      setUnitName(initialValues.unit_name);
    }
    // eslint-disable-next-line
  }, []);

  // Fetch unit when product changes (only if not in edit mode)
  useEffect(() => {
    if (!productId) return;
    // If in edit mode and productId matches initial, skip fetch
    if (mode === "edit" && initialValues.product_id && String(initialValues.product_id) === productId) {
      return;
    }
    setUnitId("");
    setUnitName("");
    const fetchUnit = async () => {
      try {
        const response = await fetch(
          `${API_URL}get-unit-by-product.php?product_id=${productId}`
        );
        const data = await response.json();
        if (data.status === true) {
          setUnitId(String(data.unit_id));
          setUnitName(data.unitname);
        }
      } catch (error) {
        // Optionally handle error
      }
    };
    fetchUnit();
  }, [productId, API_URL, mode, initialValues.product_id]);

  // Calculate total and due
  useEffect(() => {
    const total = parseFloat(quantity) * parseFloat(perUnitPrice);
    const paid = parseFloat(paidAmount);
    if (!isNaN(total)) {
      setTotalAmount(total);
      setDueAmount(!isNaN(paid) ? total - paid : total);
    } else {
      setTotalAmount(0);
      setDueAmount(0);
    }
  }, [quantity, perUnitPrice, paidAmount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      supplier_id: Number(supplierId),
      product_id: Number(productId),
      invoice_no: invoiceNo,
      quantity: Number(quantity),
      per_unit_price: Number(perUnitPrice),
      unit_id: Number(unitId),
      paid_amount: Number(paidAmount),
      due_amount: Number(dueAmount),
      purchase_date: purchaseDate,
      payment_type: paidAmount && parseFloat(paidAmount) > 0 ? paymentType.toLowerCase() : "",
      id: initialValues.id, // for edit
    });
  };

  return (
    <form onSubmit={handleSubmit} className="add-unit-form">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "20px 30px",
        }}
      >
        <div className="form-group">
          <label htmlFor="supplierId">Supplier</label>
          <select
            id="supplierId"
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            required
            disabled={isSubmitting}
          >
            <option value="">Select Supplier</option>
            {suppliers.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.suppliername}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="productId">Product</label>
          <select
            id="productId"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            required
            disabled={isSubmitting}
          >
            <option value="">Select Product</option>
            {products.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.product}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="invoiceNo">Invoice No</label>
          <input
            type="text"
            id="invoiceNo"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label htmlFor="purchaseDate">Purchase Date</label>
          <input
            type="date"
            id="purchaseDate"
            value={purchaseDate}
            onChange={(e) => setPurchaseDate(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label htmlFor="unitName">Unit</label>
          <input
            type="text"
            id="unitName"
            value={unitName}
            readOnly
            disabled
            placeholder="Select product to see unit"
          />
        </div>
        <div className="form-group">
          <label htmlFor="quantity">Quantity</label>
          <input
            type="number"
            id="quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
            disabled={isSubmitting}
            onWheel={e => e.target.blur()}
          />
        </div>
        <div className="form-group">
          <label htmlFor="perUnitPrice">Price Per Unit</label>
          <input
            type="number"
            id="perUnitPrice"
            value={perUnitPrice}
            onChange={(e) => setPerUnitPrice(e.target.value)}
            required
            disabled={isSubmitting}
            onWheel={e => e.target.blur()}
          />
        </div>
        <div className="form-group">
          <label htmlFor="paidAmount">Paid Amount</label>
          <input
            type="number"
            id="paidAmount"
            value={paidAmount}
            onChange={(e) => setPaidAmount(e.target.value)}
            required
            disabled={isSubmitting || mode === "edit"}
            onWheel={e => e.target.blur()}
          />
        </div>
        {paidAmount !== "" && !isNaN(parseFloat(paidAmount)) && parseFloat(paidAmount) > 0 && (
          <div className="form-group">
            <label htmlFor="paymentType">Payment Type</label>
            <select
              id="paymentType"
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              required
              disabled={isSubmitting}
            >
              <option value="">Select Payment Type</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="card">Card</option>
            </select>
          </div>
        )}
        <div className="form-group">
          <label>Total Amount</label>
          <input
            type="number"
            value={totalAmount.toFixed(2)}
            readOnly
            disabled
            onWheel={e => e.target.blur()}
          />
        </div>
        <div className="form-group">
          <label>Due Amount</label>
          <input
            type="number"
            value={dueAmount.toFixed(2)}
            readOnly
            disabled
            style={{
              fontWeight: "bold",
              color: dueAmount > 0 ? "red" : "green",
            }}
            onWheel={e => e.target.blur()}
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? (mode === "edit" ? "Saving..." : "Saving...") : mode === "edit" ? "Save Changes" : "Save Purchase"}
        </button>
      </div>
    </form>
  );
};

export default PurchaseForm; 